// Section for import npm components -----------------------------------------------------------------------------------
import React, { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

// MUI components
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// MUI icons
import DirectionsSubwayIcon from '@mui/icons-material/DirectionsSubway';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

// Section for import custom components --------------------------------------------------------------------------------
import MansionFilter from '@components/MansionNavigation/MansionFilter';
import filterEntries from '@components/MansionNavigation/Utils/Filters';
import generateSortQuery from '@components/MansionNavigation/Utils/Sort';

// Section for type definition -----------------------------------------------------------------------------------------
import MetaStationsSearchFilter from '@/types/components/MansionNavigation/MetaStationsSearchFilter';

interface StationsSearchFilterProps {
  currentSearchFilter: MetaStationsSearchFilter;
  total: number;
}

function StationsSearchFilter({ currentSearchFilter, total }: StationsSearchFilterProps) {
  const isMobile = useMediaQuery('(max-width:767px)');
  const btnText = isMobile ? '変更' : '変更する';

  /*
  絞り込み結果のテキスト生成
  ============================================================ */
  const filterTextArr = [];

  // 最寄駅までの徒歩時間のテキスト
  const belowLimitStationWalkDistance = currentSearchFilter.filters.below_limit_station_walk_distance;
  if (belowLimitStationWalkDistance) {
    const belowLimitStationWalkDistanceText = `${belowLimitStationWalkDistance}分以内`;
    filterTextArr.push(belowLimitStationWalkDistanceText);
  }
  // 築年数のテキスト
  const belowLimitBuildCreatedYear =
    typeof currentSearchFilter.filters.below_limit_build_created_year === 'number'
      ? currentSearchFilter.filters.below_limit_build_created_year
      : false;
  const aboveLimitBuildCreatedYear =
    typeof currentSearchFilter.filters.above_limit_build_created_year === 'number'
      ? currentSearchFilter.filters.above_limit_build_created_year
      : false;

  function generateBuildYearText(belowLimit: number | false, aboveLimit: number | false): string {
    if (belowLimit === false && typeof aboveLimit === 'number') {
      return `${aboveLimit}年以上`;
    }
    if (aboveLimit === false && typeof belowLimit === 'number') {
      return `${belowLimit}年以内`;
    }
    if (typeof belowLimit === 'number' && typeof aboveLimit === 'number') {
      return `${aboveLimit}年以上 〜 ${belowLimit}年以下`;
    }
    return '';
  }
  const buildYearText = generateBuildYearText(belowLimitBuildCreatedYear, aboveLimitBuildCreatedYear);
  if (buildYearText) {
    filterTextArr.push(buildYearText);
  }

  // 土地権利のテキスト
  const landRight = currentSearchFilter.filters.land_right;
  function formatLandRights(landRights: string[] | false): string {
    if (!landRights) {
      return '';
    }
    const rightsMap: { [key: string]: string } = {
      ownership: '所有権',
      fixed_term_lease: '定期借地権',
      leasehold_ground_right: '賃借権・地上権',
    };
    return landRights.map((right) => rightsMap[right]).join(', ');
  }
  const landRightText = formatLandRights(landRight);
  if (landRightText) {
    filterTextArr.push(landRightText);
  }

  // 戸数のテキスト
  const aboveLimitBuildHouses = currentSearchFilter.filters.above_limit_build_houses;
  if (aboveLimitBuildHouses) {
    const aboveLimitBuildHousesText = `${aboveLimitBuildHouses}戸以上`;
    filterTextArr.push(aboveLimitBuildHousesText);
  }

  // 売出し中の物件を表示のテキスト
  const isSaleCheck = currentSearchFilter.filters.is_sale;
  const isSaleCheckText = isSaleCheck === true ? '売出し中' : '';
  if (isSaleCheckText) {
    filterTextArr.push(isSaleCheckText);
  }

  // ペット可の物件を表示のテキスト
  const isPetsCheck = currentSearchFilter.filters.is_pets;
  const isPetsCheckText = isPetsCheck === true ? 'ペット飼育可（相談）' : '';
  if (isPetsCheckText) {
    filterTextArr.push(isPetsCheckText);
  }

  // 絞り込み結果のテキストを生成
  const filterResultsText = filterTextArr.length > 0 ? filterTextArr.join(', ') : '指定なし';

  // 路線用クエリ
  const filtersWithoutStationIdLineId = { ...currentSearchFilter.filters };
  delete filtersWithoutStationIdLineId.station_id;
  delete filtersWithoutStationIdLineId.line_id;
  const lineFilterQuery = filterEntries(filtersWithoutStationIdLineId)
    ? `&${filterEntries(filtersWithoutStationIdLineId)}${generateSortQuery(currentSearchFilter.sort, '&')}`
    : `${generateSortQuery(currentSearchFilter.sort, '&')}`;

  // 駅用クエリ
  const filtersWithoutStationId = { ...currentSearchFilter.filters };
  delete filtersWithoutStationId.station_id;
  const stationFilterQuery = filterEntries(filtersWithoutStationId)
    ? `&${filterEntries(filtersWithoutStationId)}${generateSortQuery(currentSearchFilter.sort, '&')}`
    : `${generateSortQuery(currentSearchFilter.sort, '&')}`;

  return (
    <Box className="w-full md:w-275 relative bg-back">
      <Table className="w-full md:w-275">
        <TableBody>
          <TableRow>
            <TableCell className="w-1/6 align-middle border-t bg-gray-01 whitespace-nowrap">
              <Box className="flex items-center">
                <DirectionsSubwayIcon color="disabled" className="w-6 h-6 mr-2" />
                <Box className="text-xs md:text-base text-black font-bold">路線</Box>
              </Box>
            </TableCell>
            <TableCell className="align-middle border-t pl-6 max-md:pl-3">
              <Box className="flex justify-between">
                <Typography className="text-sm max-md:text-xs  leading-8 max-md:leading-7">
                  {currentSearchFilter.lines.name}
                </Typography>
                <Button>
                  <Link
                    href={`${currentSearchFilter.lines.url_history}${lineFilterQuery}`}
                    underline="none"
                    className="flex gap-x-1"
                  >
                    <Typography className="text-sm max-md:text-xs text-rf-blue">{btnText}</Typography>
                    <ArrowForwardIosRoundedIcon className="h-5 max-md:h-4 text-sm max-md:text-xs text-rf-black" />
                  </Link>
                </Button>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="w-1/6 align-middle border-t bg-gray-01 whitespace-nowrap">
              <Box className="flex items-center">
                <DirectionsSubwayIcon color="disabled" className="w-6 h-6 mr-2" />
                <Box className="text-xs md:text-base text-black font-bold">駅</Box>
              </Box>
            </TableCell>
            <TableCell className="align-middle border-t pl-6 max-md:pl-3">
              <Box className="flex justify-between">
                <Typography className="text-sm max-md:text-xs leading-8 max-md:leading-7">
                  {currentSearchFilter.stations.name}
                </Typography>
                <Button>
                  <Link
                    href={`${currentSearchFilter.stations.url_history}${stationFilterQuery}`}
                    underline="none"
                    className="flex gap-x-1"
                  >
                    <Typography className="text-sm max-md:text-xs text-rf-blue">{btnText}</Typography>
                    <ArrowForwardIosRoundedIcon className="h-5 max-md:h-4 text-sm max-md:text-xs text-rf-black" />
                  </Link>
                </Button>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="w-1/6 align-middle bg-gray-01 whitespace-nowrap">
              <Box className="flex items-center">
                <FilterAltIcon color="disabled" className="w-6 h-6 mr-2" />
                <Box className="text-xs md:text-base text-black font-bold">絞込み</Box>
              </Box>
            </TableCell>
            <TableCell className="align-middle pl-6 max-md:pl-3">
              <Box className="w-full flex justify-between">
                <Typography className="w-8/12 line-clamp-2 text-sm max-md:text-xs leading-8 max-md:leading-7">
                  {filterResultsText}
                </Typography>
                <Box>
                  <MansionFilter
                    filters={currentSearchFilter.filters}
                    url={currentSearchFilter.url}
                    total={total}
                    sort={currentSearchFilter.sort}
                  />
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

export default StationsSearchFilter;
